import { useAuthenticated, useUser } from '@tbitop/authentication';
import { isBrowser } from '@tbitop/ssr';
import { defaultTo, isNull, isUndefined } from 'lodash-es';
import type { JSX, PropsWithChildren } from 'react';
import type { AuthorizationConfig } from './authorization-config';
import { log } from './log';
import { userRolesMatchAnyRequiredUserRoles } from './resource-authorization';

type IfAuthorizedProps = {
  notAuthorized?: JSX.Element | null;
  authenticating?: JSX.Element | null;
  config?: AuthorizationConfig;
  logWarning?: boolean;
};

export const IfAuthorized = ({ children, notAuthorized, authenticating = null, config, logWarning = true }: PropsWithChildren<IfAuthorizedProps>) => {
  const authenticated = useAuthenticated();
  const user = useUser();

  if (!isBrowser || isNull(user) || !authenticated) {
    return authenticating;
  }

  if (isUndefined(config)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const isAuthorized = userRolesMatchAnyRequiredUserRoles(
    config.resourceName,
    config.requiredRoles,
    defaultTo(user.activeOrganization.role, []),
  );

  if (isAuthorized) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (logWarning) {
    log.warn(`${user.email} is missing permissions to access ${config.resourceName}.`);
  }

  return notAuthorized ?? null;
};
